/**
 * FoodPulseDealsGrid — public listing of live deals with real-time
 * countdown timers, discount badges, and vendor stickers.
 *
 * Route: /food-pulse/deals
 * Reads: GET /api/deals?active_only=true
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Tag, Clock, MapPin, ArrowRight, Filter, Search, Camera } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const AMBER_SOFT = "#F6C87A";
const CREAM = "#FBF7EF";

function useCountdown(endsAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = endsAt ? new Date(endsAt).getTime() : null;
  if (!end) return { expired: false, label: "no expiry" };
  const diff = end - now;
  if (diff <= 0) return { expired: true, label: "Expired" };
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0)   return { expired: false, label: `${d}d ${h}h left` };
  if (h > 0)   return { expired: false, label: `${h}h ${m}m left`, urgent: h < 3 };
  if (m > 0)   return { expired: false, label: `${m}m ${sec}s left`, urgent: true };
  return { expired: false, label: `${sec}s left`, urgent: true };
}

function DealCard({ deal }) {
  const ctd = useCountdown(deal.ends_at);
  if (ctd.expired) return null;
  const savings = deal.original_price && deal.deal_price
    ? Math.round(((deal.original_price - deal.deal_price) / deal.original_price) * 100)
    : deal.discount_pct;
  const image = deal.image_urls?.[0];

  return (
    <Link
      to={`/food-pulse/deals/${deal.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden border transition-all hover:-translate-y-1 hover:shadow-xl"
      style={{ borderColor: "#E9D8B4" }}
      data-testid={`deal-card-${deal.slug}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br"
           style={{ background: image ? undefined : `linear-gradient(135deg, ${AMBER_SOFT}, ${CREAM})` }}>
        {image
          ? <img src={image} alt={deal.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><Tag className="w-14 h-14 opacity-30" style={{ color: GREEN }} /></div>
        }
        {savings ? (
          <div className="absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-bold text-white shadow-lg" style={{ background: "#DC2626" }}>
            −{savings}%
          </div>
        ) : null}
        {deal.source === "community_scout" && (
          <div className="absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest shadow-md flex items-center gap-1" style={{ background: "rgba(228,161,64,0.95)", color: GREEN }}>
            <Camera className="w-3 h-3" /> Scout · {deal.scout_name?.split(" ")[0] || "user"}
          </div>
        )}
        {deal.source === "producer" && (
          <div className="absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest text-white shadow-md flex items-center gap-1" style={{ background: "rgba(15,61,46,0.95)" }}>
            {deal.producer_type === "fisherman" || deal.producer_type === "hobby_fisher" ? "🎣" :
             deal.producer_type === "small_farmer" ? "🌾" : "🏡"}
            {" "}
            {(deal.producer_type || "producer").replace("_", " ")}
          </div>
        )}
        {typeof deal.distance_km === "number" && (
          <div className="absolute top-3 right-14 rounded-full px-2 py-0.5 text-[10px] font-mono text-white shadow" style={{ background: "rgba(15,61,46,0.85)" }}>
            {deal.distance_km} km
          </div>
        )}
        <div className={`absolute top-3 right-3 rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-white shadow-md ${ctd.urgent ? "animate-pulse" : ""}`}
             style={{ background: ctd.urgent ? "#DC2626" : "rgba(15,61,46,0.9)" }}>
          <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />{ctd.label}
        </div>
      </div>
      <div className="p-4">
        {deal.vendor_name && (
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1 truncate" style={{ color: AMBER }}>
            {deal.vendor_name}
          </div>
        )}
        <h3 className="font-display text-lg leading-tight mb-2 line-clamp-2" style={{ color: GREEN }}>
          {deal.title}
        </h3>
        <div className="flex items-end justify-between gap-2">
          <div>
            {deal.deal_price != null && (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold" style={{ color: GREEN }}>${deal.deal_price}</span>
                {deal.original_price && deal.original_price > deal.deal_price && (
                  <span className="text-xs line-through opacity-50">${deal.original_price}</span>
                )}
                {deal.unit && <span className="text-xs opacity-70">/{deal.unit}</span>}
              </div>
            )}
            {deal.quantity_available != null && (
              <div className="text-[11px] mt-1 opacity-70">
                {deal.quantity_available} {deal.unit || ""} left
              </div>
            )}
          </div>
          {(deal.city || deal.state) && (
            <div className="flex items-center gap-1 text-[11px] opacity-60">
              <MapPin className="w-3 h-3" />
              {deal.city}{deal.state ? `, ${deal.state}` : ""}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function FoodPulseDealsGrid() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const city = params.get("city") || "";
  const state = params.get("state") || "";
  const [deals, setDeals] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/api/deals`, {
      params: { active_only: true, limit: 60, q: q || undefined, city: city || undefined, state: state || undefined },
    }).then((r) => {
      if (!cancel) setDeals(r.data.items || []);
    }).catch(() => { if (!cancel) setDeals([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [q, city, state]);

  // Nearby harvest — asks for GPS on first render, silently falls back
  // if the user declines.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        axios.get(`${API}/api/deals/nearby`, {
          params: { lat: pos.coords.latitude, lon: pos.coords.longitude, radius_km: 25, limit: 12 },
        }).then((r) => setNearby(r.data.items || []))
          .catch(() => setNearby([]));
      },
      () => {},
      { timeout: 6000, maximumAge: 600000 },
    );
  }, []);

  const visible = useMemo(() => deals.filter(d => !d.ends_at || new Date(d.ends_at) > new Date()), [deals]);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="foodpulse-deals-grid">
      <SeoHead
        section="food-pulse-deals"
        title="Today's Food Pulse deals — surplus, coupons & real prices"
        description="Live surplus-food deals, restaurant coupons, and farm-fresh markdowns near you. Update every hour, expire fast."
      />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: GREEN, color: "#fff" }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(115deg, ${GREEN} 0%, rgba(15,61,46,0.85) 60%, rgba(15,61,46,0.4) 100%), url('')`,
            backgroundSize: "cover", backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="relative max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.28em] mb-4"
               style={{ borderColor: AMBER, color: AMBER_SOFT }}>
            <Clock className="w-3.5 h-3.5" /> Live deals — right now
          </div>
          <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            Today's <span style={{ color: AMBER }}>real deals.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg opacity-90">
            Surplus produce, day-old bread, ugly-but-perfect vegetables, and closing-hour markdowns from real vendors nearby. Every deal expires — so grab them while they're hot.
          </p>

          {/* Search bar */}
          <div className="mt-8 flex gap-3 items-center max-w-2xl">
            <div className="flex-1 flex items-center gap-2 rounded-full px-4 py-2.5 bg-white/95">
              <Search className="w-4 h-4 opacity-50" style={{ color: GREEN }} />
              <input
                type="text"
                defaultValue={q}
                onKeyDown={(e) => { if (e.key === "Enter") setParams(prev => ({ ...Object.fromEntries(prev), q: e.target.value })); }}
                placeholder="Search deals: bananas, sourdough, avocado…"
                className="flex-1 bg-transparent outline-none text-sm"
                style={{ color: GREEN }}
                data-testid="deals-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-24 opacity-60" data-testid="deals-loading">Loading fresh deals…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24" data-testid="deals-empty">
            <Tag className="w-14 h-14 mx-auto opacity-25 mb-4" />
            <div className="font-display text-2xl mb-2">No live deals right now.</div>
            <div className="opacity-70 text-sm max-w-md mx-auto">
              Check back at 3&nbsp;PM Pacific — that's when local vendors typically post their surplus markdowns.
              You can also <Link to="/food-pulse/businesses" className="underline">browse vendors</Link> or
              <Link to="/food-pulse/vendor" className="underline ml-1">list a deal yourself</Link>.
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Live now</div>
                <h2 className="font-display text-3xl">{visible.length} deal{visible.length === 1 ? "" : "s"} available</h2>
              </div>
              <Link to="/food-pulse/vendor" className="text-sm font-semibold underline underline-offset-4" style={{ color: GREEN }}
                    data-testid="deals-become-vendor">
                Are you a vendor? Post a deal →
              </Link>
            </div>
            <div className="rounded-2xl border p-3 mb-6 flex items-center gap-3" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
              <Camera className="w-5 h-5" style={{ color: AMBER }} />
              <div className="text-sm flex-1">
                <strong>Spot a deal at any store?</strong> Publish it in 60 seconds and earn Food Scout points.
              </div>
              <Link to="/food-pulse/spot" className="rounded-full px-4 py-2 text-white text-sm font-semibold flex-shrink-0" style={{ background: GREEN }} data-testid="deals-spot-cta">
                Spot a deal →
              </Link>
            </div>
            <div className="rounded-2xl border p-3 mb-6 flex items-center gap-3" style={{ borderColor: GREEN, background: "#F0F5F1" }}>
              <span className="text-xl" aria-hidden>🌾</span>
              <div className="text-sm flex-1">
                <strong>Fisherman, farmer, or home grower?</strong> Post your fresh harvest with GPS + direct contact.
              </div>
              <Link to="/food-pulse/harvest" className="rounded-full px-4 py-2 text-white text-sm font-semibold flex-shrink-0" style={{ background: AMBER, color: GREEN }} data-testid="deals-harvest-cta">
                Post harvest →
              </Link>
            </div>

            {/* Nearby harvest / deals band */}
            {nearby.length > 0 && (
              <div className="mb-8" data-testid="deals-nearby-section">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Near you</div>
                    <h3 className="font-display text-2xl">🌾 {nearby.length} within {nearby[0]?.distance_km ? "25" : "10"} km</h3>
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {nearby.slice(0, 6).map((d) => <DealCard key={"n-" + d.slug} deal={d} />)}
                </div>
              </div>
            )}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((d) => <DealCard key={d.slug} deal={d} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
